import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

export default function ReferralCard() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/referrals/me").then((r) => setInfo(r.data)).catch((err) => logError("Failed to load referral info", err)); }, []);
  if (!info) return null;

  const copy = async () => {
    await navigator.clipboard.writeText(info.link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };
  const share = async () => {
    const text = `I use Futuregen Innovations for CQC & Ofsted compliance support — sign up with my link and get ${info.percent_off}% off your first month.`;
    if (navigator.share) {
      try { await navigator.share({ title: "Futuregen Innovations", text, url: info.link }); } catch (err) { if (err?.name !== "AbortError") logError("Share failed", err); }
    } else {
      copy();
    }
  };

  return (
    <div id="referral-card" className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid="referral-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <span className="overline text-accent">Refer a colleague</span>
          <h3 className="font-display text-lg font-bold mt-1">Give {info.percent_off}% off their first month</h3>
          <p className="text-sm text-muted-foreground mt-1">Share your link with fellow managers. Every sign-up is tracked here.</p>
        </div>
        <div className="w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0"><Gift className="w-5 h-5" /></div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <code className="flex-1 min-w-0 truncate text-xs sm:text-sm bg-muted rounded-lg px-3 py-2.5 font-mono" data-testid="referral-link">{info.link}</code>
        <Button variant="outline" onClick={copy} data-testid="referral-copy-button">
          {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />} {copied ? "Copied" : "Copy"}
        </Button>
        <Button onClick={share} data-testid="referral-share-button"><Share2 className="w-4 h-4 mr-1.5" /> Share</Button>
      </div>
      <div className="flex gap-6 mt-4 text-sm flex-wrap">
        <p><span className="font-bold" data-testid="referral-count">{info.referred}</span> <span className="text-muted-foreground">signed up</span></p>
        <p><span className="font-bold" data-testid="referral-converted">{info.converted}</span> <span className="text-muted-foreground">subscribed</span></p>
        <p><span className="font-bold" data-testid="referral-free-months">{info.free_months_earned}</span> <span className="text-muted-foreground">free month{info.free_months_earned === 1 ? "" : "s"} earned</span></p>
        <p className="text-muted-foreground">Code <span className="font-mono font-semibold text-foreground" data-testid="referral-code">{info.code}</span></p>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">You get <span className="font-semibold text-foreground">one free month</span> every time a colleague you refer subscribes — credited to your next invoice.
        {info.free_months_pending > 0 && <span data-testid="referral-pending-note"> {info.free_months_pending} free month{info.free_months_pending === 1 ? "" : "s"} waiting to be applied when your subscription is active.</span>}
      </p>
      {info.discount_available && (
        <p className="mt-3 text-xs text-accent font-medium" data-testid="referral-discount-note">You were referred — {info.percent_off}% off is applied automatically at checkout on your first subscription.</p>
      )}
    </div>
  );
}
